'use client'

import { useState, useRef, useEffect } from 'react'
import Popover from '@/components/ui/Popover'
import MobileMenu from '@/components/ui/MobileMenu'
import Button from '@/components/ui/Button'
import { Icon } from '@/icons/Icon'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useSliderPause } from '@/components/ui/Slider'
import { type MenuItem } from '@/helpers/menus'

type BaseMenuProps = {
  items: MenuItem[]
  add?: MenuItem[]
  position?: 'up' | 'down'
  align?: 'left' | 'right' | 'auto'
  subAlign?: 'up' | 'down' | 'center'
  largeButton?: boolean
  children?: React.ReactNode
  buttonClass?: string
  className?: string
  portal?: boolean
  menuVisible?: boolean
  setMenuVisibleAction?: (value: boolean) => void
}

export default function Menu({
  items,
  add,
  position = 'down',
  align = 'auto',
  subAlign = 'center',
  largeButton = false,
  children: customButton,
  buttonClass = '',
  className,
  portal = false,
  menuVisible: externalMenuVisible,
  setMenuVisibleAction: externalSetMenuVisible,
}: BaseMenuProps) {
  const { md } = useBreakpoint()
  const sliderPause = useSliderPause()

  const [internalMenuVisible, setInternalMenuVisible] = useState(false)
  const [addMenuVisible, setAddMenuVisible] = useState(false)
  const [computedAlign, setComputedAlign] = useState<'left' | 'right'>('left')

  const hasAdd = add && add.length
  const isControlled = externalMenuVisible !== undefined && externalSetMenuVisible !== undefined
  const menuVisible = isControlled ? externalMenuVisible : internalMenuVisible
  const setMenuVisible = isControlled ? externalSetMenuVisible : setInternalMenuVisible
  const buttonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (align !== 'auto') return

    const updateAlignment = () => {
      if (!buttonRef.current) return

      const rect = buttonRef.current.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const buttonCenter = rect.left + rect.width / 2

      setComputedAlign(buttonCenter > windowWidth / 2 ? 'right' : 'left')
    }

    updateAlignment()

    window.addEventListener('resize', updateAlignment)
    return () => window.removeEventListener('resize', updateAlignment)
  }, [align, menuVisible])

  // Pause an ancestor Slider's autoplay while any menu is open, so the card doesn't
  // scroll out from under the open dropdown (no-op outside a Slider).
  const anyMenuOpen = menuVisible || addMenuVisible
  useEffect(() => {
    if (!anyMenuOpen || !sliderPause) return
    sliderPause.pause()
    return () => sliderPause.resume()
  }, [anyMenuOpen, sliderPause])

  const finalAlign = align === 'auto' ? computedAlign : align

  const addButton = (
    <div className={buttonClass}>
      <Button
        style={addMenuVisible ? 'active' : 'default'}
        size={largeButton ? 'large' : 'default'}
        className="!rounded-r-none !border-r-0 pl-0.5"
        aria-label="Add"
      >
        <Icon name={'plus'} />
      </Button>
    </div>
  )

  const trigger = (
    <div ref={buttonRef} className={hasAdd ? undefined : className}>
      {customButton || (
        <div className={buttonClass}>
          <Button
            style={menuVisible ? 'active' : 'default'}
            size={largeButton ? 'large' : 'default'}
            className={hasAdd ? '!rounded-l-none pr-0.5' : ''}
            aria-label="More options"
          >
            <Icon name={'dots'} />
          </Button>
        </div>
      )}
    </div>
  )

  if (!md) {
    return (
      <div className={`flex ${className ?? ''}`}>
        {hasAdd ? (
          <MobileMenu
            items={add!}
            menuVisible={addMenuVisible}
            setMenuVisibleAction={setAddMenuVisible}
          >
            {addButton}
          </MobileMenu>
        ) : null}
        <MobileMenu items={items} menuVisible={menuVisible} setMenuVisibleAction={setMenuVisible}>
          {trigger}
        </MobileMenu>
      </div>
    )
  }

  return (
    <div className={`flex ${className ?? ''}`}>
      {hasAdd ? (
        <Popover
          menu={add!}
          position={position}
          align={finalAlign}
          subAlign={subAlign}
          visible={addMenuVisible}
          setVisible={setAddMenuVisible}
          maxHeight={220}
        >
          {addButton}
        </Popover>
      ) : null}
      <Popover
        menu={items}
        portal={portal}
        position={position}
        align={finalAlign}
        subAlign={subAlign}
        visible={menuVisible}
        setVisible={setMenuVisible}
      >
        {trigger}
      </Popover>
    </div>
  )
}
